import { useCallback, useEffect, useState } from 'react'

import { fetchGeneratedImages, sendGenerateImagesRequest } from 'api/generate-images'
import useInterval from 'hooks/useInterval'

import buildGenerateImagesRequestBody from './generateImagesTransforms'

const POLL_INTERVAL_MS = 30000

export function resolveCoCreateVariantFromStatus(data) {
  if (!data) return 'closed'

  const { status, ready } = data

  if (status === 'STARTED') return 'generating'
  if (status === 'SUCCESS' && ready) return 'ready'
  if (status === 'SUCCESS' && !ready) return 'generating'
  if (status === 'FAILURE') return 'closed'

  return 'closed'
}

export default function useCoCreateImagePanel({ eventId, tc, tcRunId, onClearGenerating }) {
  const [variant, setVariantState] = useState('closed')
  const [generatedResult, setGeneratedResult] = useState(null)
  const [generationFailed, setGenerationFailed] = useState(false)
  const [fetchStatusFailed, setFetchStatusFailed] = useState(false)

  const setVariant = useCallback((next) => {
    setVariantState((prev) => (typeof next === 'function' ? next(prev) : next))
  }, [])

  useEffect(() => {
    if (variant === 'open') {
      setGenerationFailed(false)
    }
  }, [variant])

  const applyStatusResponse = useCallback((success, data, status) => {
    if (!success) {
      setGeneratedResult(null)

      if (status === 404) {
        setVariant('closed')
      } else {
        setFetchStatusFailed(true)
      }

      return
    }

    setFetchStatusFailed(false)

    if (data?.status === 'FAILURE') {
      setGenerationFailed(true)
      setGeneratedResult(null)
      setVariant('closed')
      onClearGenerating?.()
      return
    }

    const statusVariant = resolveCoCreateVariantFromStatus(data)

    if (statusVariant === 'ready') {
      setGeneratedResult(data.result ?? null)
    } else {
      setGeneratedResult(null)
    }

    setVariant((prev) => {
      if (prev === 'open' && statusVariant === 'closed') return prev
      return statusVariant
    })
  }, [setVariant, onClearGenerating])

  const checkStatus = useCallback(async () => {
    if (!eventId || !tc || tcRunId == null) return

    const response = await fetchGeneratedImages(eventId, tc, tcRunId)
    applyStatusResponse(response.success, response.data, response.status)
  }, [eventId, tc, tcRunId, applyStatusResponse])

  // Fetch only when campaign identity changes — not when parent callbacks recreate
  useEffect(() => {
    checkStatus()
  }, [eventId, tc, tcRunId]) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: avoid refetch on checkStatus identity

  useInterval(
    checkStatus,
    variant === 'generating' ? POLL_INTERVAL_MS : null,
    () => variant === 'generating',
  )

  const generateImages = useCallback(async ({
    media,
    selectedSubGroupId,
    ratioVariants,
    headlines = [],
    requiredSlotRatios,
  }) => {
    if (!eventId || !tc || tcRunId == null) {
      return { success: false }
    }

    const body = buildGenerateImagesRequestBody({
      eventId,
      tc,
      tcRunId,
      media,
      selectedSubGroupId,
      ratioVariants,
      headlines,
      requiredSlotRatios,
    })

    if (!body.media.length) {
      return { success: false }
    }

    setGenerationFailed(false)
    setVariant('generating')

    const response = await sendGenerateImagesRequest(body)

    if (!response.success) {
      setVariant('open')
      return response
    }

    await checkStatus()
    return {
      ...response,
      groupId: body.group_id ?? null,
    }
  }, [eventId, tc, tcRunId, checkStatus, setVariant])

  return {
    variant,
    setVariant,
    generatedResult,
    generationFailed,
    fetchStatusFailed,
    checkStatus,
    generateImages,
  }
}
